import {Box, Tab, Tabs, CircularProgress, Typography} from "@mui/material";
import {
    Chart as ChartJS,
    CategoryScale,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import React, {useEffect, useRef, useState} from "react";
import {Line} from "react-chartjs-2";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const LineChartHook = ({chartData, selectedPortfolio, portfolioId, isLoggedIn, roiData, errorData}) => {
    const chartRef = useRef(null);
    const [filteredChartData, setFilteredChartData] = useState(null);
    const [timeRange, setTimeRange] = useState("1w");

    const [disabledTabs, setDisabledTabs] = useState({
        "1w": false,
        "1m": false,
        "3m": false,
        "6m": true,
        "1y": true,
    });

    useEffect(() => {
        if (chartData && portfolioId && chartData[portfolioId]?.dates?.length) {
            const dates = chartData[portfolioId].dates;
            const earliestDate = dayjs(dates[0]);
            const today = dayjs();

            setDisabledTabs({
                "1w": earliestDate.isAfter(today.subtract(7, "day")),
                "1m": earliestDate.isAfter(today.subtract(1, "month")),
                "3m": earliestDate.isAfter(today.subtract(3, "month")),
                "6m": earliestDate.isAfter(today.subtract(6, "month")),
                "1y": earliestDate.isAfter(today.subtract(1, "year")),
            });
        }
    }, [chartData, portfolioId]);

    useEffect(() => {
        if (chartData && portfolioId && chartData[portfolioId]) {
            setTimeRange("1w");
            filterDataByTimeRange("1w");
        }
    }, [chartData, portfolioId]);

    const isMobile = window.innerWidth <= 768;

    const getChartOptions = (isMobile, minY) => ({
        scales: {
            x: {
                title: {
                    display: true,
                    text: "",
                    font: {size: isMobile ? 8 : 14, family: "Arial", weight: "bold"},
                    color: isLoggedIn ? "#4A4A4A" : "rgba(0,0,0,0.1)",
                },
                ticks: {
                    font: {size: isMobile ? 8 : 14, family: "Arial"},
                    color: true,
                    stepSize: undefined,
                    maxTicksLimit: Math.floor(isMobile ? 7 : 14),
                },
                grid: {display: false},
            },
            y: {
                position: "right",
                title: {
                    display: true,
                    text: "",
                    font: {size: isMobile ? 12 : 16, family: "Arial", weight: "bold"},
                    color: isLoggedIn ? "#4A4A4A" : "rgba(0,0,0,0.1)",
                },
                ticks: {
                    font: {size: isMobile ? 8 : 14, family: "Arial"},
                    color: isLoggedIn ? "#4A4A4A" : "rgba(0,0,0,0.1)",
                    callback: function (value) {
                        return value.toFixed(2) + "%";
                    },
                },
                grid: {display: true, color: "rgba(0, 0, 0, 0.05)"},
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: "top",
                labels: {
                    font: {family: "Arial", size: isMobile ? 8 : 12},
                    color: isLoggedIn ? "#4A4A4A" : "rgba(0,0,0,0.1)",
                },
            },
            title: {display: false},
            tooltip: isLoggedIn
                ? {
                      enabled: true,
                      callbacks: {
                          label: function (tooltipItem) {
                              return `Change: ${tooltipItem.raw.toFixed(1)}%`;
                          },
                      },
                      backgroundColor: "#ffffff",
                      titleFont: {family: "Arial", size: 14},
                      bodyFont: {family: "Arial", size: 12},
                      borderColor: "#333",
                      borderWidth: 1,
                      titleColor: "#4A4A4A",
                      bodyColor: "#4A4A4A",
                  }
                : false,
        },
        elements: {
            point: {radius: 0},
            line: {tension: 0.4, borderColor: "#7e7ef7", backgroundColor: "rgba(126, 126, 247, 0.3)"},
        },
        interaction: {mode: "nearest", axis: "x", intersect: false},
    });

    const filterDataByTimeRange = (range) => {
        setTimeRange(range);

        if (!chartData || !portfolioId || !chartData[portfolioId]) return;

        const portfolioData = chartData[portfolioId];
        const {dates, values} = portfolioData;

        let sliceDays = 8;
        if (range === "1m") sliceDays = 31;
        if (range === "3m") sliceDays = 91;
        if (range === "6m") sliceDays = 181;
        if (range === "1y") sliceDays = 366;

        const recentDates = dates.slice(-sliceDays);
        const recentValues = values.slice(-sliceDays);

        if (recentValues.length === 0) {
            setFilteredChartData(null);
            return;
        }

        const baseValue = recentValues[0];
        const percentValues = recentValues.map((value) => {
            console.log(value, baseValue);
            const percent = ((value - baseValue) / baseValue) * 100;
            return parseFloat(percent.toFixed(1));
        });
        setFilteredChartData({
            dates: recentDates,
            values: percentValues,
        });
    };

    const handleChange = (event, newValue) => {
        filterDataByTimeRange(newValue);
    };

    return (
        <Box className={`${!isLoggedIn ? "opacity-50" : ""} line-graph`} sx={{position: "relative"}}>
            {/* Tabs */}
            <Box sx={{borderBottom: 1, borderColor: "divider", mb: 2}}>
                <Tabs value={timeRange} onChange={handleChange} aria-label="Time Range Tabs">
                    <Tab label="1W" value="1w" disabled={disabledTabs["1w"] || errorData} />
                    <Tab label="1M" value="1m" disabled={disabledTabs["1m"] || errorData} />
                    <Tab label="3M" value="3m" disabled={disabledTabs["3m"] || errorData} />
                    <Tab label="6M" value="6m" disabled={disabledTabs["6m"] || errorData} />
                    <Tab label="1Y" value="1y" disabled={disabledTabs["1y"] || errorData} />
                </Tabs>
            </Box>

            <Box
                sx={{
                    width: "100%",
                    height: isMobile ? "400px" : "clamp(250px, 60vh, 500px)",
                    minHeight: isMobile ? "400px" : "300px",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {errorData ? (
                    <Typography variant="h6" color="textSecondary">
                        No data found
                    </Typography>
                ) : filteredChartData === null ? (
                    <CircularProgress />
                ) : (
                    <Line
                        ref={chartRef}
                        data={{
                            labels: filteredChartData?.dates || [],
                            datasets: [
                                {
                                    label: selectedPortfolio || "",
                                    data: filteredChartData?.values || [],
                                    fill: true,
                                },
                            ],
                        }}
                        options={getChartOptions(isMobile)}
                    />
                )}
            </Box>
        </Box>
    );
};

export default LineChartHook;
